import { GlobalSettingsKeys } from '../../api/models/GlobalSettingsKeys';
import { Lightning, Registry } from '@lightningjs/sdk';
import { getDuration } from '../utility/DateFormatter';
import { Buttons } from './ui/OverlayHelper';
import { PlayerSettingsManager } from './PlayerSettingsManager';
import { Backend } from '../../api/Backend';
import { UiEventHandlers } from './ui/UiEventHandlers';
import { StreamInfo, StreamInfoType } from './streamInfo/StreamInfo';
import { PlayerEvent, PlayerTimes, SeekbarInfo } from './PlayerModel';
import { LinearStreamInfo } from './streamInfo/LinearStreamInfo';
import { IStreamPlayerPolicy } from '../../api/models/AndromeTV.types';

/**
 * @typedef {Object} SeekPositions
 * @property {number} player
 * @property {number} ui
 */
export interface SeekPositions {
  player: number;
  ui: number;
}

const MIN_TRICKPLAY_SPEED = 2;
const MAX_TRICKPLAY_SPEED = 8;

/**
 * @property {SeekPositions} _seekFrom
 */
export class PlayerControlsManager {
  private _backend: Backend;
  private _uiEventHandlers: UiEventHandlers;
  private _settingsManager: PlayerSettingsManager;
  private _settings:
    | {
        fastForwardTime: number;
        hideFps: number;
        rewindTime: number;
        seekDelay: number;
        fastForwardStopTime: number;
        progressUpdatePeriod: number;
      }
    | undefined;
  private _currentStreamInfo: StreamInfo | undefined;
  private _paused = false;
  private _seekDiff = 0;
  private _seekFrom: { ui: number; player: number } = { player: -1, ui: 0 };
  private _playSpeed = 1;
  private _seekBarFocused: boolean | undefined;
  private _updatePositionTimer: number | undefined;
  private _seekTimeout: number | undefined;
  private _currentUIButtonList: string[] = [];
  private firstRewind = false;

  constructor(backend: Backend, uiEventHandlers: UiEventHandlers) {
    this._backend = backend;
    this._uiEventHandlers = uiEventHandlers;
    this._settingsManager = new PlayerSettingsManager(backend);

    this.updateGlobalSettings();
  }

  set streamInfo(currentStreamInfo: StreamInfo) {
    this._currentStreamInfo = currentStreamInfo;
    this.updateGlobalSettings();
  }

  private updateGlobalSettings() {
    this._settings = {
      progressUpdatePeriod:
        this._backend.globalSettings.numberValue(GlobalSettingsKeys.playerProgressUpdatePeriod) || 1000,
      fastForwardTime: this._backend.globalSettings.numberValue(GlobalSettingsKeys.playerFastForwardVod) || 30000,
      rewindTime: this._backend.globalSettings.numberValue(GlobalSettingsKeys.playerRewindVod) || 15000,
      seekDelay: this._backend.globalSettings.numberValue(GlobalSettingsKeys.seekTimeout) || 500,
      hideFps: this._backend.globalSettings.numberValue(GlobalSettingsKeys.playerQualityHideFps) || 25,
      fastForwardStopTime: this._backend.globalSettings.numberValue(GlobalSettingsKeys.fastForwardStopTime) || 10000,
    };
  }

  start() {
    this._reset();
    this._startUpdatePlayerPosition();
  }

  stop() {
    this._currentStreamInfo = undefined;
    this._stopUpdatePlayerPosition();
  }

  resetSeekbar() {
    this._uiEventHandlers.updatePosition({
      posPercent: 0,
      currTime: getDuration(0),
      remainTime: getDuration(0),
    });
  }

  _reset() {
    this._paused = false;
    this._seekDiff = 0;
    this._seekFrom = { player: -1, ui: 0 };
    this._playSpeed = 1;
    this._backend.playerController.playerService.configurePlaySpeed(this._playSpeed);
  }

  get isTrickPlay() {
    return this._playSpeed !== 1;
  }

  setInitialButtonConfig(metadataLoaded: boolean) {
    const buttons = [];
    if (this._backend.playerController.seekEnabled) {
      buttons.push(Buttons.REWIND);
      buttons.push(Buttons.PAUSE);
      if (this._currentStreamInfo?.type !== StreamInfoType.LINEAR) {
        buttons.push(Buttons.FORWARD);
      }
    }
    if (metadataLoaded && this._settingsManager.hasSettings()) {
      buttons.push(Buttons.SETTINGS);
    }

    if (this._backend.playerController.seekingSwitchStream) {
      this._updateUIButtons(buttons);
      this._backend.playerController.seekingSwitchStream = false;
    } else {
      this._updateUIButtons(buttons);
    }
  }

  set seekBarFocused(value: boolean) {
    this._seekBarFocused = value;
    if (this._seekBarFocused) {
      this._initSeekStart();
    } else {
      this._seekFrom = { player: -1, ui: 0 };
      this._updatePlayerPosition();
    }
  }

  _startUpdatePlayerPosition() {
    this._stopUpdatePlayerPosition();
    this._updatePlayerPosition();
    this._updatePositionTimer = Registry.setInterval(
      () => this._updatePlayerPosition(),
      this._settings?.progressUpdatePeriod
    );
  }

  _updatePlayerPosition() {
    if (this._checkStopTrickPlay()) {
      return;
    }
    if (this._seekBarFocused || !this._currentStreamInfo) {
      return;
    }
    const playerTimes = this._currentPlaytime();
    if (playerTimes) {
      this._uiEventHandlers.updatePosition(this._createSeekbarInfo(playerTimes));
      // check autoplay time
      // if at end, fetch data
      this._backend.playerController.checkAutoplay();
    }
  }

  _checkStopTrickPlay() {
    const seekRange = this._backend.playerController.playerService.seekRange;
    const position = this._backend.playerController.playerService.relativePosition;
    if (this._playSpeed < 1) {
      if (position <= 0) {
        this._handlePlay();
        return true;
      }
    } else if (this._playSpeed > 1 && seekRange) {
      const timeLeft = seekRange.end - position;
      const stopTime = this._settings?.fastForwardStopTime ?? 0;
      if (timeLeft <= stopTime / 1000) {
        this._handlePlay();
        return true;
      }
    }
    return false;
  }

  _stopUpdatePlayerPosition() {
    if (this._updatePositionTimer) {
      Registry.clearInterval(this._updatePositionTimer);
      this._updatePositionTimer = undefined;
    }
  }

  handleSeekEvent(forward: boolean) {
    // check if live, block forward
    if (forward && this._currentStreamInfo?.type === StreamInfoType.LINEAR) {
      return true;
    }
    if (this._backend.playerController.onSeek()) {
      return true;
    }
    if (this._seekFrom.player < 0) {
      this._initSeekStart();
    }
    if (forward) {
      const forwardTime = this._settings?.fastForwardTime ?? 0;
      this._seekDiff += forwardTime / 1000;
    } else {
      const rewindTime = this._settings?.rewindTime ?? 0;
      this._seekDiff -= rewindTime / 1000;
    }
    this._setSeekTimeout();
    return false;
  }

  _initSeekStart() {
    const playerService = this._backend.playerController.playerService;
    this._seekFrom.player = playerService.relativePosition;

    const playerTimes = this._currentStreamInfo?.convertPlayTimes(
      playerService.absolutePosition,
      playerService.duration,
      playerService.isLive
    );
    this._seekFrom.ui = playerTimes?.position || 0;
  }

  _setSeekTimeout() {
    if (this._seekTimeout) {
      Registry.clearTimeout(this._seekTimeout);
    }
    this._stopUpdatePlayerPosition();
    const seekInfo = this._calcPlayerPosition();
    const playerTimes = this._currentPlaytime();
    if (playerTimes) {
      playerTimes.position = seekInfo.ui;
      this._uiEventHandlers.updatePosition(this._createSeekbarInfo(playerTimes));
    }

    this._seekTimeout = Registry.setTimeout(() => {
      this._backend.playerController.playerService.seekAbsolute(seekInfo.player);
      this._updatePlayerPosition();

      this._seekDiff = 0;
      this._seekFrom = seekInfo;
      this._seekTimeout = undefined;
      this._startUpdatePlayerPosition();
    }, this._settings?.seekDelay);
  }

  /**
   * @param {PlayerTimes} playerTimes
   * @returns {SeekbarInfo}
   * @private
   */
  _createSeekbarInfo(playerTimes: PlayerTimes): SeekbarInfo {
    return {
      posPercent: Math.max(playerTimes.position, 0) / playerTimes.duration,
      currTime: getDuration(playerTimes.position - (this._currentStreamInfo?.startOffset || 0)),
      remainTime: getDuration(playerTimes.duration - playerTimes.position),
    };
  }

  /**
   * @returns {PlayerTimes}
   * @private
   */
  _currentPlaytime(): PlayerTimes | undefined {
    const playerService = this._backend.playerController.playerService;
    return this._currentStreamInfo?.convertPlayTimes(
      playerService.absolutePosition,
      playerService.duration,
      playerService.isLive
    );
  }

  /**
   * @returns {SeekPositions}
   * @private
   */
  _calcPlayerPosition(): SeekPositions {
    const seekTime = this._seekFrom.player + this._seekDiff;
    if (seekTime !== 0) {
      const seekRange = this._backend.playerController.playerService.seekRange;
      if (seekRange) {
        if (seekTime < seekRange.start) {
          this._seekDiff += seekRange.start - seekTime;
        } else if (seekTime > Math.floor(seekRange.end)) {
          this._seekDiff -= Math.floor(seekTime - seekRange.end);
        }
      }
    }

    return {
      player: this._seekFrom.player + this._seekDiff,
      ui: this._seekFrom.ui + this._seekDiff,
    };
  }

  handleActivateButton(button: string, element?: Lightning.Component) {
    switch (button) {
      case Buttons.PLAY:
        this._handlePlay();
        return false;
      case Buttons.PAUSE:
        this._handlePause();
        return false;
      case Buttons.FORWARD:
        this.handleFastForward();
        return false;
      case Buttons.REWIND:
        this.handleFastRewind();
        return false;
      case Buttons.SETTINGS:
        this._backend.playerController.overlayHelper?.hideAll();
        if (element) this._settingsManager.configStream(element);
        return true;
      default:
        return false;
    }
  }

  _handlePause() {
    if (!this._backend.playerController.seekEnabled) {
      return;
    }
    this._playSpeed = 1;
    this._configureTrickPlay();
    this._backend.playerController.playerService.pause();
    this._paused = true;
    const buttons = this._getUIButtons();
    this._replaceButton(buttons, Buttons.PAUSE, Buttons.PLAY);
    this._updateUIButtons(buttons);
  }

  _handlePlay() {
    if (!this._backend.playerController.seekEnabled) {
      return;
    }
    this._playSpeed = 1;
    this._paused = false;
    this._configureTrickPlay();
    this._backend.playerController.play();
  }

  togglePlayPause() {
    if (!this._backend.playerController.seekEnabled) {
      return;
    }
    if (this._paused || this._playSpeed !== 1) {
      this._handlePlay();
      this._paused = false;
    } else {
      this._handlePause();
      this._paused = true;
    }
  }

  // Samsung TVs only support 2x fast forward
  handleFastForward() {
    if (!this._backend.playerController.seekEnabled) {
      return;
    }
    if (this._playSpeed < MIN_TRICKPLAY_SPEED) {
      this._playSpeed = MIN_TRICKPLAY_SPEED;
    }
    this._configureTrickPlay();
  }

  handleFastRewind() {
    if (!this._backend.playerController.seekEnabled) {
      return;
    }
    if (this._backend.playerController.streamInfo instanceof LinearStreamInfo) {
      this.firstRewind = true;
    }
    this._backend.playerController.onTrickPlay().then(() => {
      if (this._playSpeed > -MIN_TRICKPLAY_SPEED) {
        this._playSpeed = -MIN_TRICKPLAY_SPEED;
      } else {
        this._playSpeed *= 2;
        if (this._playSpeed < -MAX_TRICKPLAY_SPEED) {
          this._playSpeed = -MIN_TRICKPLAY_SPEED;
        }
      }
      const localSpeed = this._playSpeed;

      if (this.firstRewind) {
        this._backend.playerController.playerService.on(PlayerEvent.PLAYING, () => {
          if (this.firstRewind) {
            this._playSpeed = localSpeed;
            this._configureTrickPlay();
            this.firstRewind = false;
          }
        });
      } else {
        this._configureTrickPlay();
      }
    });
  }

  _configureTrickPlay() {
    const playPauseBtn = this._playSpeed !== 1 ? Buttons.PLAY : Buttons.PAUSE;
    const buttons = this._getUIButtons();
    const replaceBtn = playPauseBtn === Buttons.PLAY ? Buttons.PAUSE : Buttons.PLAY;
    this._replaceButton(buttons, replaceBtn, playPauseBtn);
    this._updateUIButtons(buttons);

    if (this._paused) {
      this._backend.playerController.playerService.play();
      this._paused = false;
    }
    this._backend.playerController.playerService.configurePlaySpeed(this._playSpeed);
    this._backend.playerController.uiEventHandlers.updateTrickPlay(this._playSpeed > 0, Math.abs(this._playSpeed));
    this._backend.playerController.overlayHelper?.setTrickPlay(this._playSpeed !== 1);
  }

  _updateUIButtons(buttons: string[]) {
    this._currentUIButtonList = buttons;
    this._backend.playerController.uiEventHandlers.updateControlButtons(buttons);
  }

  _getUIButtons() {
    return this._currentUIButtonList;
  }

  _replaceButton(buttons: string[], search: string, target: string) {
    const idx = buttons.indexOf(search);
    buttons[idx] = target;
    return buttons;
  }

  _handleBack() {
    if (this._playSpeed !== 1) {
      this._handlePlay();
      return true;
    }
    return false;
  }

  public setPlayerPolicies(policies?: IStreamPlayerPolicy) {
    this._settingsManager.hideAudioSelection = !!policies?.hideAudioSelection;
  }
}
